<template>
  <div class="journey-day-card">
    <!-- Top row with day, chip, tabs, and actions -->
    <div class="journey-day-card__top-row">
      <div class="journey-day-card__day">{{ day }}</div>

      <div class="journey-day-card__right-group">
        <!-- <journey-chip
          :label="computedChipLabel"
          :color="computedChipColor"
          variant="filled"
          :key="`chip-${editingTime}-${computedChipLabel}-${computedChipColor}`"
        /> -->

        <journey-tabs
          v-if="tabs && tabs.length > 0"
          :items="tabs"
          :value="editingTime"
          :disabled="!isEditing"
          @input="onTabChange"
          class="journey-day-card__tabs"
        />

        <div v-if="showActions" class="journey-day-card__actions">
          <v-btn
            icon
            small
            color="grey"
            :disabled="disabled || isRegenerating || isSaving"
            :loading="isSaving"
            @click="$emit('edit')"
            class="journey-day-card__action-btn"
          >
            <v-icon v-if="!isSaving" size="16">{{
              isEditing ? 'bhi-check' : 'bhi-pencil'
            }}</v-icon>
          </v-btn>
          <v-btn
            v-if="!isEditing"
            icon
            small
            color="grey"
            :disabled="disabled"
            :loading="isRegenerating"
            @click="$emit('regenerate')"
            class="journey-day-card__action-btn"
          >
            <v-icon v-if="!isRegenerating" size="16">bhi-cached</v-icon>
          </v-btn>
          <v-btn
            v-else
            icon
            small
            color="grey"
            @click="$emit('cancel-edit')"
            class="journey-day-card__action-btn"
          >
            <v-icon size="16">bhi-close-circle</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="journey-day-card__content">
      <!-- Title section -->
      <div v-if="title && title !== ''" class="journey-day-card__title-section">
        <h3 v-if="!isEditing" class="journey-day-card__title">
          {{ title }}
        </h3>
        <v-text-field
          v-else
          v-model="editingTitle"
          :placeholder="'Enter title'"
          dense
          outlined
          hide-details
          class="journey-day-card__title-input"
          @input="updateEditingData('title', $event)"
        />
      </div>

      <!-- Description section -->
      <div class="journey-day-card__description-section">
        <p v-if="!isEditing" class="journey-day-card__description">
          {{ description }}
        </p>
        <v-textarea
          v-else
          ref="descriptionInput"
          v-model="editingDescription"
          :placeholder="'Enter description'"
          dense
          outlined
          hide-details
          rows="3"
          class="journey-day-card__description-input"
          @input="updateEditingData('description', $event)"
        />
      </div>

      <!-- Time/Order editing for specific types -->
      <div
        v-if="isEditing && editingData"
        class="journey-day-card__edit-fields"
      >
        <!-- Timed Messages - Day and Time editing -->
        <div
          v-if="editingData.type === 'timedMessage'"
          class="journey-day-card__timed-message-fields"
        >
          <div class="journey-day-card__time-picker">
            <v-text-field
              v-model.number="editingDay"
              label="Day"
              type="number"
              dense
              outlined
              hide-details
              min="1"
              class="journey-day-card__day-input"
              @blur="checkIfDayIsCorrect"
              @input="updateEditingData('day', $event)"
            />
            <v-text-field
              v-model.number="editingTimeHour"
              label="Hour"
              type="number"
              min="1"
              max="12"
              dense
              outlined
              hide-details
              @input="updateTime"
            />
            <v-text-field
              v-model.number="editingTimeMinute"
              label="Minute"
              type="number"
              min="0"
              max="59"
              dense
              outlined
              hide-details
              @input="updateTime"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import JourneyChip from './JourneyChip.vue'
import JourneyTabs from './JourneyTabs.vue'
import { getChipColor } from '@/helpers/journeyChipHelpers'

export default {
  name: 'JourneyDayCard',
  components: {
    // JourneyChip,
    JourneyTabs,
  },
  props: {
    day: {
      type: [String, Number],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    chipLabel: {
      type: String,
      required: false,
    },
    chipColor: {
      type: String,
      default: 'green',
    },
    tabs: {
      type: Array,
      default: () => ['AM', 'PM'],
    },
    activeTab: {
      type: String,
      default: 'AM',
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    isRegenerating: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    editingData: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'tab-change',
    'edit',
    'regenerate',
    'update-editing-data',
    'cancel-edit',
  ],
  computed: {
    editingTitle: {
      get() {
        return this.editingData?.title || this.title
      },
      set(value) {
        this.updateEditingData('title', value)
      },
    },
    editingDescription: {
      get() {
        return this.editingData?.description || this.description
      },
      set(value) {
        this.updateEditingData('description', value)
      },
    },
    editingTime: {
      get() {
        // For timed messages, use a separate timeOfDay property to track AM/PM
        if (this.editingData?.timeOfDay) {
          return this.editingData.timeOfDay
        }
        return this.activeTab || 'AM'
      },
      set(value) {
        // Store the time of day separately from the actual time
        this.updateEditingData('timeOfDay', value)
      },
    },
    editingDay: {
      get() {
        return this.editingData?.day || 1
      },
      set(value) {
        this.updateEditingData('day', value)
      },
    },
    editingTimeHour: {
      get() {
        // For timed messages, use the stored 12-hour value
        if (
          this.editingData?.type === 'timedMessage' &&
          this.editingData?.hour12 !== undefined
        ) {
          return this.editingData.hour12
        }

        if (typeof this.editingData?.time === 'string') {
          // Parse HH:mm:ss format and convert to 12-hour
          const hour24 = parseInt(this.editingData.time.split(':')[0], 10) || 0
          if (hour24 === 0) return 12
          if (hour24 > 12) return hour24 - 12
          return hour24
        }
        if (this.editingData?.time?.hour !== undefined) {
          const hour24 = this.editingData.time.hour
          if (hour24 === 0) return 12
          if (hour24 > 12) return hour24 - 12
          return hour24
        }
        return 12
      },
      set(value) {
        // Store the 12-hour value separately for display
        this.updateEditingData('hour12', value)
        this.updateTime()
      },
    },
    editingTimeMinute: {
      get() {
        // For timed messages, use the stored minute value
        if (
          this.editingData?.type === 'timedMessage' &&
          this.editingData?.minute12 !== undefined
        ) {
          return this.editingData.minute12
        }

        if (typeof this.editingData?.time === 'string') {
          // Parse HH:mm:ss format
          return parseInt(this.editingData.time.split(':')[1], 10) || 0
        }
        return this.editingData?.time?.minute || 0
      },
      set(value) {
        // Store the minute value separately for display
        this.updateEditingData('minute12', value)
        this.updateTime()
      },
    },
    editingOrder: {
      get() {
        return this.editingData?.order || 0
      },
      set(value) {
        this.updateEditingData('order', value)
      },
    },
    computedChipLabel() {
      return this.chipLabel
    },
    computedChipColor() {
      // If editing, use the current editing time to determine chip color
      if (this.isEditing && this.editingData?.time) {
        // Force reactivity by accessing the time property
        const time = this.editingData.time
        return getChipColor(time)
      }
      // Otherwise use the original chip color
      return this.chipColor
    },
  },
  watch: {
    isEditing(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.autoResizeTextarea()
        })
      }
    },
  },
  methods: {
    checkIfDayIsCorrect() {
      if (
        !this.editingData?.day ||
        this.editingData?.day <= 1 ||
        isNaN(parseInt(`${this.editingData?.day}`, 10))
      ) {
        this.editingDay = 1
        this.updateEditingData('day', '1')
      }
    },
    updateEditingData(field, value) {
      this.$emit('update-editing-data', field, value)

      if (field === 'description' && this.isEditing) {
        this.$nextTick(() => {
          this.autoResizeTextarea()
        })
      }
    },
    autoResizeTextarea() {
      const textarea = this.$refs.descriptionInput
      if (textarea && textarea.$el) {
        // For Vuetify components, we need to access the actual textarea element
        const textareaElement = textarea.$el.querySelector('textarea')
        if (textareaElement) {
          textareaElement.style.height = 'auto'
          textareaElement.style.height = textareaElement.scrollHeight + 'px'
        }
      } else if (textarea && textarea.style) {
        // Fallback for direct textarea element
        textarea.style.height = 'auto'
        textarea.style.height = textarea.scrollHeight + 'px'
      }
    },
    onTabChange(value) {
      // Update the timeOfDay property
      this.updateEditingData('timeOfDay', value)

      // For timed messages, trigger time conversion
      if (this.editingData?.type === 'timedMessage') {
        this.updateTime()
      } else if (
        !this.editingData?.time ||
        typeof this.editingData.time === 'string'
      ) {
        // For non-timed messages, also update the time format
        const timeValue = value === 'AM' ? 'MORNING' : 'EVENING'
        this.updateEditingData('time', timeValue)
      }
    },
    updateTime() {
      const { hour, minute } = this.getCurrentTimeValues()
      this.initializeTimeProperties(hour, minute)
      this.initializeTimeOfDay()

      const finalHour = this.convertTo24HourFormat(hour)
      const timeString = this.formatTimeString(finalHour, minute)

      this.updateEditingData('time', timeString)
    },

    getCurrentTimeValues() {
      // For timed messages, use the 12-hour value from hour12 property
      if (
        this.editingData?.type === 'timedMessage' &&
        this.editingData?.hour12 !== undefined
      ) {
        // If we have hour12 but no minute12, try to get minute from original time
        let minute = this.editingData.minute12
        if (minute === undefined) {
          minute = this.getMinuteFromOriginalTime()
        }
        return {
          hour: this.editingData.hour12,
          minute: minute,
        }
      }

      // Parse from string format (HH:mm:ss)
      if (typeof this.editingData?.time === 'string') {
        const [h, m] = this.editingData.time.split(':').map(Number)
        return this.convert24To12Hour(h || 0, m || 0)
      }

      // Parse from object format
      if (this.editingData?.time && typeof this.editingData.time === 'object') {
        return this.convert24To12Hour(
          this.editingData.time.hour || 0,
          this.editingData.time.minute || 0,
        )
      }

      // Fallback
      return { hour: 0, minute: 0 }
    },

    convert24To12Hour(hour24, minute24) {
      let hour12 = hour24
      if (hour24 === 0) {
        hour12 = 12
      } else if (hour24 > 12) {
        hour12 = hour24 - 12
      }
      return { hour: hour12, minute: minute24 }
    },

    convert12To24Hour(hour12, timeOfDay) {
      if (timeOfDay === 'AM') {
        return hour12 === 12 ? 0 : hour12
      } else if (timeOfDay === 'PM') {
        return hour12 === 12 ? 12 : hour12 + 12
      }
      return hour12
    },

    initializeTimeProperties(hour, minute) {
      if (this.editingData?.type === 'timedMessage') {
        // Only initialize hour12 if it's not already set
        if (this.editingData.hour12 === undefined) {
          this.editingData.hour12 = hour
        }
        // Only initialize minute12 if it's not already set
        if (this.editingData.minute12 === undefined) {
          this.editingData.minute12 = minute
        }
      }
    },

    initializeTimeOfDay() {
      if (
        this.editingData?.type === 'timedMessage' &&
        !this.editingData?.timeOfDay
      ) {
        const originalHour24 = this.getOriginalHour24()
        this.editingData.timeOfDay = originalHour24 >= 12 ? 'PM' : 'AM'
      }
    },

    getOriginalHour24() {
      if (typeof this.editingData?.time === 'string') {
        return parseInt(this.editingData.time.split(':')[0], 10) || 0
      }
      if (this.editingData?.time?.hour !== undefined) {
        return this.editingData.time.hour
      }
      return 0
    },

    getMinuteFromOriginalTime() {
      if (typeof this.editingData?.time === 'string') {
        return parseInt(this.editingData.time.split(':')[1], 10) || 0
      }
      if (this.editingData?.time?.minute !== undefined) {
        return this.editingData.time.minute
      }
      return 0
    },

    convertTo24HourFormat(hour12) {
      if (this.editingData?.timeOfDay) {
        return this.convert12To24Hour(hour12, this.editingData.timeOfDay)
      }
      return hour12
    },

    /**
     * Format time as HH:mm:ss string
     */
    formatTimeString(hour, minute) {
      const hourStr = String(hour).padStart(2, '0')
      const minuteStr = String(minute).padStart(2, '0')
      return `${hourStr}:${minuteStr}:00`
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.journey-day-card {
  background-color: $bh-gray--6;
  border-radius: 16px;
  padding: 16px;

  &__top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__day {
    color: $bh-gray--2;
    font-size: 14px;
    font-weight: 500;
    flex-shrink: 0;
  }

  &__right-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__tabs {
    flex-shrink: 0;
  }

  &__actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  &__action-btn {
    min-width: 24px !important;
    width: 24px !important;
    height: 24px !important;

    .v-icon {
      color: $bh-gray--3;
    }

    &:hover .v-icon {
      color: $bh-gray--2;
    }
  }

  &__content {
    margin-top: 8px;
  }

  &__title {
    color: $bh-black;
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 4px 0;
    line-height: 1.4;
  }

  &__description {
    color: $bh-gray--2;
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
    white-space: pre-line;
  }

  &__title-section,
  &__description-section {
    margin-bottom: 8px;
  }

  &__title-input,
  &__description-input {
    .v-input__control {
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }

  &__edit-fields {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid $bh-gray--5;
  }

  &__time-section,
  &__timed-message-fields,
  &__order-section {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__time-picker {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__time-select,
  &__day-input,
  &__order-input {
    min-width: 120px;
    flex: 1;

    .v-input__control {
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }

  &__time-picker .v-text-field {
    min-width: 80px;
    flex: 1;

    .v-input__control {
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }
}
</style>
